import {
  ACCESS_APP_LABELS,
  ACCESS_MODULE_LABELS,
  appOf,
  isLevelOf,
  LADDERS,
  ladderOf,
  TEMPLATE_MODULES,
  templateGrantsError,
  type AccessModule,
  type RoleTemplate,
  type TemplateGrant,
  type TemplateScope,
} from '@church/permissions'
import { useState } from 'react'
import { levelOptions } from './useGrantForm.ts'

const SCOPE_LABELS: Record<TemplateScope, string> = {
  org: 'Church-wide',
  pick: 'Chosen when applied',
}

const moduleLabel = (module: AccessModule) =>
  `${appOf(module) === 'services' ? 'ForServices' : 'ForChurch'} · ${ACCESS_MODULE_LABELS[module]}`

/**
 * A role template being made or changed (Settings → Roles): its name, and
 * the levels it gives — each a module, a rung of its ladder, and
 * church-wide or "where you choose when applying" (planning only). The
 * rules (`templateGrantsError`) say whether it may be saved.
 */
export function useTemplateDraft(input: { resetKey: string | null; start: RoleTemplate | null }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [grants, setGrants] = useState<TemplateGrant[]>([])
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  if (input.resetKey !== loadedFor) {
    setLoadedFor(input.resetKey)
    setName(input.start?.name ?? '')
    setDescription(input.start?.description ?? '')
    setGrants(
      input.start
        ? [...input.start.grants]
        : [{ module: 'planning', level: 'viewer', scope: 'pick' }],
    )
  }

  /** Changing a grant's module keeps its level and place where they still fit. */
  const fit = (grant: TemplateGrant): TemplateGrant => ({
    module: grant.module,
    level: isLevelOf(grant.module, grant.level)
      ? grant.level
      : (LADDERS[ladderOf(grant.module)][0] ?? 'viewer'),
    scope: grant.module === 'planning' ? grant.scope : 'org',
  })

  const problem = name.trim() ? templateGrantsError(grants) : 'Name the template.'

  return {
    name,
    setName,
    description,
    setDescription,
    grants,
    moduleOptions: TEMPLATE_MODULES.map((id) => ({ id, label: moduleLabel(id) })),
    /** The modules grouped by app for the "What" select: ForServices, then ForChurch. */
    moduleGroups: (['services', 'church'] as const).map((app) => ({
      app: ACCESS_APP_LABELS[app],
      options: TEMPLATE_MODULES.filter((id) => appOf(id) === app).map((id) => ({
        id,
        label: ACCESS_MODULE_LABELS[id],
      })),
    })),
    levelOptions: (module: AccessModule) => levelOptions(module, LADDERS[ladderOf(module)]),
    scopeOptions: (module: AccessModule) =>
      (module === 'planning' ? (['pick', 'org'] as const) : (['org'] as const)).map((id) => ({
        id,
        label: SCOPE_LABELS[id],
      })),
    setGrant: (index: number, patch: Partial<TemplateGrant>) =>
      setGrants((list) =>
        list.map((grant, i) => (i === index ? fit({ ...grant, ...patch }) : grant)),
      ),
    addGrant: () =>
      setGrants((list) => {
        // The next module it does not give yet, church-wide.
        const module =
          TEMPLATE_MODULES.find((id) => !list.some((grant) => grant.module === id)) ?? 'planning'
        return [...list, fit({ module, level: 'viewer', scope: 'org' })]
      }),
    removeGrant: (index: number) => setGrants((list) => list.filter((_, i) => i !== index)),
    /** Why it cannot be saved yet; null when it can. */
    problem,
    draft: { name: name.trim(), description: description.trim() || null, grants },
  }
}
